import { ALMIKIA_STAGE_VALUES, INBOX_STAGE } from "./stages";
import type { Deal } from "../types";

/**
 * The 24h SLA window a Prospecto lead must be contacted within
 * (product-rules.md, state 0 "Prospecto"). This is a commercial service
 * rule (lead reassignment by SLA), never disciplinary — no per-advisor
 * counts, totals or comparisons are derived from it (Independence guide).
 */
export const PROSPECT_SLA_HOURS = 24;

/**
 * ASSUMPTION (TASK-016): "about to exceed" the SLA is the last 4 hours of
 * the 24h window, so a lead flips from on-time to due-soon once it reaches
 * `PROSPECT_SLA_HOURS - PROSPECT_SLA_WARNING_HOURS` = 20h old.
 */
export const PROSPECT_SLA_WARNING_HOURS = 4;

/** How close a Prospecto lead is to breaching (or has breached) its 24h SLA. */
export type ProspectInboxFlag = "on_time" | "due_soon" | "overdue";

/**
 * Classifies a lead's position in the 24h SLA window from its creation
 * time: under 20h old is on time, 20h up to (not including) 24h is due
 * soon, 24h or older is overdue. Pure function — unit-tested at the
 * boundaries.
 */
export function classifyProspectAge(
  createdAt: string,
  now: Date = new Date(),
): ProspectInboxFlag {
  const hoursElapsed =
    (now.getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
  if (hoursElapsed >= PROSPECT_SLA_HOURS) return "overdue";
  if (hoursElapsed >= PROSPECT_SLA_HOURS - PROSPECT_SLA_WARNING_HOURS)
    return "due_soon";
  return "on_time";
}

/** Hours elapsed since a lead's creation, floored, for display. */
export function getElapsedHours(
  createdAt: string,
  now: Date = new Date(),
): number {
  return Math.max(
    0,
    Math.floor(
      (now.getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60),
    ),
  );
}

/**
 * Selects the leads shown in the Prospecto inbox: leads whose stage is the
 * inbox stage, plus leads carrying a legacy/unknown stage value (displayed
 * as Prospecto, their stored stage is never rewritten — see
 * `stages.ts#findDealLabel`). `knownStageValues` (Almikia Epic EP, TASK-104)
 * is the effective, configured stage-value set: a direccion-created stage is
 * a KNOWN value, so it is excluded exactly like an E2 kanban/special stage —
 * only a value neither configured nor an E2 default counts as legacy/unknown
 * and stays in the inbox. Defaults to the 10 E2 values so an existing caller
 * that has not been wired to the effective list keeps today's behavior.
 * Ordered oldest first, so the leads closest to breaching the SLA surface
 * first. Expects `deals` already scoped to the non-archived, owner-filtered
 * set the leads page requests (same source the kanban reads from
 * `useListContext`).
 */
export function selectProspectInboxDeals(
  deals: Deal[],
  knownStageValues: readonly string[] = ALMIKIA_STAGE_VALUES,
): Deal[] {
  return deals
    .filter(
      (deal) =>
        deal.stage === INBOX_STAGE || !knownStageValues.includes(deal.stage),
    )
    .slice()
    .sort(
      (dealA, dealB) =>
        new Date(dealA.created_at).getTime() -
        new Date(dealB.created_at).getTime(),
    );
}
